import { useEffect, useState } from 'react';
import type { Report } from '@zevik/schemas';
import type { Source } from '../api/client';
import { readReport, type ReportContent } from '../api/report';
import { apiSentence, sessionLost } from '../credential/session';
import { Refused } from './credential/CredentialShell';
import { ReportReader } from './ReportReader';

/**
 * The assessment's reports: read in the page, and download
 * (docs/DECISIONS.md ZD-125 §5).
 *
 * The list is every report `GET /:id/reports` returns, newest first, and it
 * outlives the state that produced it: a revision uploaded after a report
 * sends the assessment back to the start, and the report a client was
 * given is still theirs to read, so this panel stands wherever the list is
 * not empty rather than only at the state that generates one.
 *
 * Reading opens the newest report by default — one document is what a
 * client came for — and both the page and the download are the same bytes,
 * fetched once. A second format, when one exists, would be a second entry
 * here saying which it is; nothing on this panel assumes the report is
 * markdown except the reader that renders it.
 */
export function Reports({
  source,
  reports,
  onSessionLost,
}: {
  source: Source;
  reports: readonly Report[];
  onSessionLost: (reason: string) => void;
}): React.JSX.Element | null {
  const newest = reports[0] ?? null;
  const [open, setOpen] = useState<Report | null>(newest);
  const [content, setContent] = useState<{ report_id: string; content: ReportContent } | null>(
    null,
  );
  const [failure, setFailure] = useState<string | null>(null);

  // A newer report replaces the one open, so a person who just generated one reads it.
  useEffect(() => {
    setOpen(newest);
  }, [newest]);

  useEffect(() => {
    if (open === null) return;
    let live = true;
    setFailure(null);
    readReport(source, open)
      .then((read) => {
        if (live) setContent({ report_id: open.report_id, content: read });
      })
      .catch((error: unknown) => {
        if (!live) return;
        if (sessionLost(error)) onSessionLost(apiSentence(error));
        else setFailure(error instanceof Error ? error.message : String(error));
      });
    return () => {
      live = false;
    };
  }, [source, open, onSessionLost]);

  if (reports.length === 0) return null;

  const download = (): void => {
    if (content === null) return;
    const blob = new Blob([content.content.text], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = content.content.filename;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="reports" aria-label="Reports">
      <h2>Report</h2>
      {reports.length === 1 ? null : (
        <ul className="reports__list">
          {reports.map((report) => (
            <li key={report.report_id}>
              <button
                type="button"
                className="submit--link"
                aria-current={open?.report_id === report.report_id ? 'true' : undefined}
                onClick={() => {
                  setOpen(report);
                }}
              >
                {new Date(report.generated_at).toLocaleString()}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open === null ? null : (
        <p className="reports__meta">
          Generated {new Date(open.generated_at).toLocaleString()} from{' '}
          {String(open.source_finding_ids.length)}{' '}
          {open.source_finding_ids.length === 1 ? 'finding' : 'findings'}.{' '}
          <button
            type="button"
            className="submit--link"
            disabled={content?.report_id !== open.report_id}
            onClick={download}
          >
            Download
          </button>
        </p>
      )}
      {failure === null ? null : <Refused message={failure} />}
      {open === null || content?.report_id !== open.report_id ? (
        failure === null ? (
          <p className="reports__meta">Reading the report…</p>
        ) : null
      ) : (
        <ReportReader text={content.content.text} />
      )}
    </section>
  );
}
